/**
 * Palette mapping utilities for Remotion video templates
 *
 * Maps AnimatedGradient palettes to RetroGrid presets for consistent
 * background styling across different background types.
 */

import type { RetroGridPreset } from '../components/RetroGridBackground';

/**
 * Maps AnimatedGradient palette names to their closest RetroGrid preset equivalent.
 *
 * Direct matches use the same preset name.
 * Non-matching palettes are mapped to visually similar presets.
 */
export const PALETTE_TO_RETRO_GRID: Record<string, RetroGridPreset> = {
  // Direct matches
  synthwave: 'synthwave',
  cyberpunk: 'cyberpunk',
  neon: 'neon',
  vaporwave: 'vaporwave',
  midnight: 'midnight',
  sunset: 'sunset',
  matrix: 'matrix',
  tron: 'tron',
  // AnimatedGradient palettes mapped to similar RetroGrid presets
  aurora: 'cyberpunk', // Cool colors
  aqua: 'tron', // Cyan/blue
  ocean: 'tron', // Blue
  sky: 'cyberpunk', // Blue
  candy: 'vaporwave', // Pink
  rose: 'vaporwave', // Pink
  peach: 'sunset', // Warm
  lemon: 'sunset', // Warm/yellow
  lava: 'synthwave', // Red/orange
  forest: 'matrix', // Green
  mint: 'matrix', // Green
  grape: 'synthwave', // Purple
  sand: 'sunset', // Warm
  luxury: 'sunset', // Gold
};

/**
 * Get the RetroGrid preset for a given palette name.
 * Falls back to a default preset if the palette is not found.
 *
 * @param palette - The palette name to look up
 * @param fallback - Default preset if palette not found (default: 'synthwave')
 * @returns The matching RetroGridPreset
 */
export function getRetroGridPreset(
  palette: string,
  fallback: RetroGridPreset = 'synthwave'
): RetroGridPreset {
  return PALETTE_TO_RETRO_GRID[palette] || fallback;
}
